import { submitCashfreePayment, buildCashfreePaymentMethod, pickCashfreeUpiIntentUrl } from '../../utils/cashfreeUtils';

// Bug fix (found live on STG, 2026-09-13): a custom URI scheme (upi://,
// gpay://, phonepe://, paytm://, ...) gives JS NO signal at all on whether
// the OS actually found an app to handle it — window.location.href just
// silently does nothing if no app is installed, no error, no rejected
// promise. The previous fixed-delay approach (call onSuccess() after
// 300ms regardless) meant tapping GPay/Paytm on a device where they're not
// installed silently jumped straight to "Checking your payment status…"
// with no payment ever actually initiated. Standard fix: listen for the
// tab actually going to the background (document.hidden) — that's the one
// reliable signal an app DID launch and take over the foreground, since
// mobile OSes background the browser when switching to another app. If the
// tab is still visible after a short window, no app answered — surface
// that as a real failure instead of guessing success.
//
// Updated 2026-10-16: on iOS / in-app browsers visibilitychange alone proved
// unreliable (app opened and payment completed, yet the "not installed" error
// still showed) — also treat pagehide and window blur as "an app took over",
// and give it a little longer before concluding nothing opened.
function attemptAppOpenAndDetect(url, { onOpened, onNotOpened, timeoutMs = 2500 }) {
  let settled = false;
  const cleanup = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onAppTookOver);
    window.removeEventListener('blur', onAppTookOver);
    clearTimeout(timer);
  };
  const onAppTookOver = () => {
    if (!settled) {
      settled = true;
      cleanup();
      onOpened();
    }
  };
  const onVisibilityChange = () => {
    if (document.hidden) onAppTookOver();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onAppTookOver);
  window.addEventListener('blur', onAppTookOver);
  const timer = setTimeout(() => {
    if (!settled) {
      settled = true;
      cleanup();
      onNotOpened();
    }
  }, timeoutMs);
  window.location.href = url;
}

/**
 * Cashfree checkout strategy — pure custom/headless, mirrors runStandardCheckout's
 * role for Razorpay (see StandardCheckout.js) but submits straight to Cashfree's
 * Order Pay API instead of opening a Razorpay SDK instance.
 *
 * `order` must carry `id` (Cashfree order_id), `paymentSessionId`, and
 * `cashfreeBaseUrl` — all present on the normalized order object the backend
 * already returns (services/paymentGatewayService.js::createOrder on the
 * backend). `method`/`app`/`details` are exactly the same shape CheckoutPage.jsx's
 * handlePay already passes to doCreatePayment today — no caller-side changes
 * needed for CardSection/NetbankingSection/WalletSection/UpiCollectSection.
 *
 * Cashfree's response `action` field decides what happens next:
 *  - "link"   -> redirect (data.url) — same role as Razorpay's redirect:true +
 *                callback_url; hand off to the browser, the return_url flow +
 *                existing order-status polling take it from there.
 *  - "post"   -> native-OTP custom flow — NOT implemented in this pass (see
 *                plan's confirmed gaps); fails clearly instead of hanging.
 *  - "custom" -> e.g. UPI collect — nothing to redirect to, the customer
 *                approves the request in their UPI app; resolves immediately
 *                so the caller starts polling, same as Razorpay's UPI-collect
 *                experience today.
 *
 * @param {object} params
 * @param {object} params.order
 * @param {string} params.method
 * @param {string} [params.app]
 * @param {object} [params.details]
 * @param {string} [params.customerPhone] - required for method:'wallet' —
 *   see buildCashfreePaymentMethod's doc comment
 * @param {boolean} [params.softNotOpened] - UPI intent only: when no app-open
 *   signal arrives, call onSuccess (flagged appOpenUnconfirmed) instead of
 *   failing — for callers that confirm via order status.
 * @param {Function} params.onSuccess
 * @param {Function} params.onFailure
 */
export async function runCashfreeCheckout({ order, method, app, details, customerPhone, softNotOpened = false, onSuccess, onFailure }) {
  try {
    const paymentMethod = buildCashfreePaymentMethod({ method, app, details, customerPhone });
    const result = await submitCashfreePayment(order.cashfreeBaseUrl, order.paymentSessionId, paymentMethod);

    // Temporary debug log (2026-09-13) — GPay vs PhonePe/Paytm are showing
    // very different behavior in sandbox (PhonePe redirects to a visible
    // Cashfree test page, GPay jumps straight to "verifying" with nothing
    // visible) and neither Cashfree's docs nor a live network capture have
    // confirmed WHY yet — could be a different `action` value entirely
    // (e.g. 'custom', a push-request style with no redirect at all) rather
    // than the same 'link' action with just a different URL scheme. Log
    // the raw response so the actual shape is visible in the console on
    // the next test tap instead of guessing further. Safe to remove once
    // confirmed — doesn't affect payment behavior either way.
    console.log('[Cashfree Order Pay response]', { method, app, provider: paymentMethod, result });

    // UPI app tiles (2026-10-16): the response can be action:'custom' with
    // per-app deep links under data.payload (gpay/phonepe/paytm/bhim/default),
    // not just action:'link' + data.url — handle both here, ahead of the
    // generic action switch below which would otherwise treat 'custom' as a
    // UPI-collect and never open any app.
    if (method === 'upi-intent') {
      const intentUrl = pickCashfreeUpiIntentUrl(result, app);
      console.log('[Cashfree UPI intent]', { app, intentUrl, payloadKeys: Object.keys(result?.data?.payload || {}) });
      if (!intentUrl) {
        onFailure({
          error: {
            code: 'CASHFREE_NO_UPI_INTENT',
            description: 'Could not open the UPI app. Please try another payment method.',
          },
        });
        return;
      }
      if (/^https?:\/\//i.test(intentUrl)) {
        window.location.href = intentUrl;
        return;
      }
      attemptAppOpenAndDetect(intentUrl, {
        onOpened: () => onSuccess(result),
        // softNotOpened (2026-10-16): the browser can't reliably tell whether
        // a UPI app opened (iOS/in-app browsers often never fire a signal, yet
        // the payment still completes) — so for callers that verify via order
        // status (CheckoutPage's PaymentStatusModal), "no signal" is NOT a
        // failure: resolve and let the order status decide. Callers that
        // don't opt in (WalletRecharge) keep the old hard failure.
        onNotOpened: () => (softNotOpened
          ? onSuccess({ ...result, appOpenUnconfirmed: true })
          : onFailure({
            error: {
              code: 'CASHFREE_APP_NOT_INSTALLED',
              description: 'This app doesn\'t appear to be installed on your device. Please choose another payment method.',
            },
          })),
      });
      return;
    }

    if (result.action === 'link' && result.data?.url) {
      // UPI intent / GPay / PhonePe / Paytm's action:'link' returns a
      // upi://... or app-specific (gpay://, phonepe://, undocumented by
      // Cashfree) deep link, not a real https:// redirect — the OS just
      // opens the target app while THIS page stays put, it never actually
      // navigates away (unlike card/netbanking's action:'link', which
      // genuinely redirects to an external bank page that itself returns
      // via return_url).
      const isHttpRedirect = /^https?:\/\//i.test(result.data.url);

      if (isHttpRedirect) {
        // Genuine external redirect — browser is navigating away, no
        // onSuccess/onFailure needed, the return_url flow on the other
        // side takes over.
        window.location.href = result.data.url;
        return;
      }

      // See attemptAppOpenAndDetect's comment above the full history here:
      // first found the promise never resolving at all (infinite spinner,
      // no polling), then found a fixed delay wasn't reliable (see its own
      // history in git blame) — this now uses the standard
      // visibility-change detection instead of guessing a delay.
      attemptAppOpenAndDetect(result.data.url, {
        onOpened: () => onSuccess(result),
        onNotOpened: () => onFailure({
          error: {
            code: 'CASHFREE_APP_NOT_INSTALLED',
            description: 'This app doesn\'t appear to be installed on your device. Please choose another payment method.',
          },
        }),
      });
      return;
    }

    if (result.action === 'post') {
      onFailure({
        error: {
          code: 'CASHFREE_NATIVE_OTP_UNSUPPORTED',
          description: 'This card requires an extra verification step not yet supported. Please try a different card or payment method.',
        },
      });
      return;
    }

    // action === 'custom' (e.g. UPI collect) — resolve so the caller starts
    // polling for the webhook-driven completion, same as Razorpay's flow.
    onSuccess(result);
  } catch (err) {
    onFailure({
      error: {
        code: 'CASHFREE_PAYMENT_ERROR',
        description: err.message || 'Payment failed. Please try again.',
      },
    });
  }
}
